import { useState } from "react";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { useToast } from "@/components/ui/toast";
import { errorMessage } from "@/lib/api";
import {
  useAdminAthlete,
  useAdminDeleteAthlete,
  useAdminUpdateAthlete,
} from "@/features/admin/hooks/useAdminData";
import type { AthleteDeletion, AthleteDeletionBlocker } from "@/lib/types";
import { deletionBlockersFromError, FIELD_CLASS } from "../athleteDisplay";

/**
 * Archive / restore / delete for one athlete (#908 row quick actions and
 * the detail header). Club admin only — the callers show these actions
 * only where the API set `can_archive`.
 *
 * - Archive asks first: the athlete leaves the default directory, nothing
 *   is deleted, roster history and links stay.
 * - Restore is immediate (`useRestoreAthlete`), with a toast.
 * - Delete is offered only for an archived athlete and loads the detail's
 *   `deletion` first: allowed ⇒ a type-the-name confirmation (the club
 *   delete pattern); blocked ⇒ the blocking kinds with counts and no
 *   delete button.
 */

type AthleteRef = { id: string; display_name: string };

export function ArchiveAthleteDialog({
  athlete,
  onClose,
  onArchived,
}: {
  athlete: AthleteRef;
  onClose: () => void;
  onArchived?: () => void;
}) {
  const toast = useToast();
  const mut = useAdminUpdateAthlete();
  const [error, setError] = useState<string | null>(null);
  const archive = async () => {
    setError(null);
    try {
      await mut.mutateAsync({
        athleteId: athlete.id,
        body: { status: "archived" },
      });
      toast.success("Athlete archived");
      onArchived?.();
      onClose();
    } catch (err) {
      const message = errorMessage(err);
      setError(message);
      toast.error("Couldn't archive the athlete", { description: message });
    }
  };
  return (
    <ConfirmDialog
      title="Archive athlete?"
      confirmLabel={mut.isPending ? "Archiving…" : "Archive athlete"}
      confirmDisabled={mut.isPending}
      onConfirm={() => void archive()}
      onClose={onClose}
      data-testid="admin-athlete-archive-dialog"
      message={
        <div className="flex flex-col gap-2 text-xs">
          <p className="text-ink">
            <span className="font-display">{athlete.display_name}</span> is
            hidden from the default directory.
          </p>
          <p>
            Nothing is deleted: roster history and linked accounts stay, and
            you can restore the athlete at any time.
          </p>
          {error ? (
            <p
              data-testid="admin-athlete-archive-error"
              className="font-mono text-destructive"
            >
              {error}
            </p>
          ) : null}
        </div>
      }
    />
  );
}

/** Restore an archived athlete straight away (no confirmation).
 * `pendingId` is the athlete whose restore is in flight; a second restore
 * while one is pending is ignored. */
export function useRestoreAthlete() {
  const toast = useToast();
  const mut = useAdminUpdateAthlete();
  const restore = async (athlete: AthleteRef) => {
    if (mut.isPending) return;
    try {
      await mut.mutateAsync({
        athleteId: athlete.id,
        body: { status: "active" },
      });
      toast.success("Athlete restored", { description: athlete.display_name });
    } catch (err) {
      toast.error("Couldn't restore the athlete", {
        description: errorMessage(err),
      });
    }
  };
  return {
    restore,
    isPending: mut.isPending,
    pendingId: mut.isPending ? (mut.variables?.athleteId ?? null) : null,
  };
}

export function DeleteAthleteDialog({
  athlete,
  onClose,
  onDeleted,
}: {
  athlete: AthleteRef;
  onClose: () => void;
  /** After the delete succeeded (toast shown, reads invalidated). */
  onDeleted?: () => void;
}) {
  const toast = useToast();
  const mut = useAdminDeleteAthlete();
  const [typedName, setTypedName] = useState("");
  const [error, setError] = useState<string | null>(null);
  // A 409 raced in after the dialog loaded: show the server's fresh list.
  const [raced, setRaced] = useState<AthleteDeletionBlocker[] | null>(null);
  // Stop reading the detail once the delete is in flight (it would 404).
  const detailQuery = useAdminAthlete(athlete.id, {
    enabled: !mut.isPending && !mut.isSuccess,
  });
  const loaded = detailQuery.data;
  const deletion: AthleteDeletion | null | undefined = raced
    ? { allowed: false, blockers: raced }
    : loaded
      ? loaded.deletion
      : undefined;

  const submit = async () => {
    setError(null);
    try {
      await mut.mutateAsync(athlete.id);
      toast.success("Athlete deleted", { description: athlete.display_name });
      onDeleted?.();
      onClose();
    } catch (err) {
      const blockers = deletionBlockersFromError(err);
      if (blockers) setRaced(blockers);
      else setError(errorMessage(err));
      toast.error("Couldn't delete the athlete", {
        description: errorMessage(err),
      });
    }
  };

  return (
    <DeleteAthleteDialogView
      athleteName={athlete.display_name}
      archived={(loaded?.athlete.status ?? "archived") === "archived"}
      loading={detailQuery.isLoading && !loaded}
      loadError={
        detailQuery.error && !loaded ? errorMessage(detailQuery.error) : null
      }
      deletion={deletion}
      typedName={typedName}
      onTypedName={setTypedName}
      pending={mut.isPending}
      error={error}
      onConfirm={() => void submit()}
      onClose={onClose}
    />
  );
}

export type DeleteDialogState = "loading" | "error" | "allowed" | "blocked";

/** The dialog's state from what has loaded: the server's `deletion` call
 * decides; `null` (not a club admin) or a non-archived athlete is blocked. */
export function deleteDialogState({
  loading,
  loadError,
  deletion,
  archived,
}: {
  loading: boolean;
  loadError: string | null;
  deletion: AthleteDeletion | null | undefined;
  archived: boolean;
}): DeleteDialogState {
  if (loadError) return "error";
  if (loading || deletion === undefined) return "loading";
  return archived && deletion?.allowed ? "allowed" : "blocked";
}

/** Presentational half of `DeleteAthleteDialog` (unit-tested on its own). */
export function DeleteAthleteDialogView({
  athleteName,
  archived,
  loading,
  loadError,
  deletion,
  typedName,
  onTypedName,
  pending,
  error,
  onConfirm,
  onClose,
}: {
  athleteName: string;
  archived: boolean;
  loading: boolean;
  loadError: string | null;
  deletion: AthleteDeletion | null | undefined;
  typedName: string;
  onTypedName: (value: string) => void;
  pending: boolean;
  error: string | null;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const state = deleteDialogState({ loading, loadError, deletion, archived });
  const blockers = deletion?.blockers ?? [];
  const common = {
    title: "Delete athlete?",
    onClose,
    "data-testid": "admin-athlete-delete-dialog",
  };

  if (state === "allowed") {
    const matches = typedName.trim() === athleteName.trim();
    return (
      <ConfirmDialog
        {...common}
        danger
        confirmLabel={pending ? "Deleting…" : "Delete athlete"}
        confirmDisabled={pending || !matches}
        onConfirm={onConfirm}
        message={
          <div className="flex flex-col gap-3 text-xs" data-delete-state={state}>
            <p className="rounded-control border border-destructive/40 bg-destructive/10 px-3 py-2 text-destructive-text">
              This permanently deletes{" "}
              <strong className="font-display">{athleteName}</strong> and their
              roster history. No other data is attached. This cannot be undone.
            </p>
            <label className="flex flex-col gap-1 text-ink">
              <span className="text-ink-muted">
                Type the athlete&rsquo;s name to confirm:{" "}
                <strong className="text-ink">{athleteName}</strong>
              </span>
              <input
                type="text"
                value={typedName}
                onChange={(e) => onTypedName(e.target.value)}
                autoComplete="off"
                autoFocus
                className={FIELD_CLASS}
                data-testid="admin-athlete-delete-confirm-name"
              />
            </label>
            {error ? (
              <p
                data-testid="admin-athlete-delete-error"
                className="font-mono text-destructive-text"
              >
                {error}
              </p>
            ) : null}
          </div>
        }
      />
    );
  }

  // Loading, load error and blocked offer no delete button: Close only.
  return (
    <ConfirmDialog
      {...common}
      cancelLabel={null}
      confirmLabel="Close"
      initialFocus="confirm"
      confirmDisabled={false}
      onConfirm={onClose}
      message={
        <div className="flex flex-col gap-3 text-xs" data-delete-state={state}>
          {state === "loading" ? (
            <p data-testid="admin-athlete-delete-loading">
              Checking what is attached to {athleteName}…
            </p>
          ) : state === "error" ? (
            <p
              data-testid="admin-athlete-delete-error"
              className="font-mono text-destructive-text"
            >
              {loadError}
            </p>
          ) : (
            <>
              <p className="text-ink">
                <span className="font-display">{athleteName}</span> can&rsquo;t
                be deleted.{" "}
                {!archived
                  ? "Archive the athlete before deleting."
                  : blockers.length
                    ? "Data is attached to this athlete:"
                    : "You don’t have permission to delete this athlete."}
              </p>
              {archived && blockers.length ? (
                <>
                  <ul
                    data-testid="admin-athlete-delete-blockers"
                    className="flex flex-col divide-y divide-rule rounded-control border border-rule"
                  >
                    {blockers.map((blocker) => (
                      <li
                        key={blocker.kind}
                        data-testid="admin-athlete-delete-blocker"
                        data-kind={blocker.kind}
                        className="flex items-center justify-between gap-3 px-3 py-2"
                      >
                        <span className="text-ink">{blocker.label}</span>
                        <span className="font-mono text-ink-muted">
                          {blocker.count}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <p>
                    Revoke linked accounts that no longer apply, or keep the
                    record archived: an archived athlete stays out of the
                    default directory.
                  </p>
                </>
              ) : null}
            </>
          )}
        </div>
      }
    />
  );
}
